import PropTypes from 'prop-types';
import React from 'react';
import ObjectFitImages from 'object-fit-images';

import {
  MEMBER_SHAPE,
  COMPANY_SHAPE,
  CONTRIBUTIONS_SHAPE,
} from '../../../../config/prop-types';

import getPartyName from '../../services/getPartyName';
import getDistrict from '../../services/getDistrict';
import getFormattedTotal from '../../services/getFormattedTotal';

import Table from '../table';

import democratIcon from './democrat.svg';
import houseIcon from './house.png';
import republicanIcon from './republican.svg';
import senateIcon from './senate.png';

/**
 * Displays the expanded details for a congressmember
 */
export default class Profile extends React.PureComponent {
  componentDidMount() {
    // call the polyfill to support object-fill in IE for profile pic sizing
    ObjectFitImages('.profile__image img');
  }
  getHeader() {
    const { entity, type } = this.props;

    const partyIcon = (- 1 !== ['R'].indexOf(entity.party)) ?
      republicanIcon : democratIcon;

    const chamberIcon = entity.chamber &&
      'senate' === entity.chamber.toLowerCase() ? senateIcon : houseIcon;

    const entityStateIcon = entity.state ?
      `stateface stateface-${entity.state.toLowerCase()}` : undefined;

    return (
      <div className="profile__memberDetails">
        <div className="profile__memberName-first">
          {'MEMBER' === type ? (
            `${('House' === entity.chamber) ? 'Rep.' : 'Sen.'}
             ${entity.firstName}`
          ) : (
            entity.parent
          )}
        </div>
        <div className="profile__memberName-last">
          {'MEMBER' === type ? entity.lastName : entity.name}
        </div>
        {'MEMBER' === type ? (
          <div className="profile__memberoffices">
            <div className="profile__memberoffice">
              {partyIcon &&
                <div
                  className="profile__bullet"
                  alt=""
                  style={{ backgroundImage: `url(${partyIcon})` }}
                />
              }
              <div className="profile__memberoffice--lbl">
                {getPartyName(entity.party)}
              </div>
            </div>
            <div className="profile__memberoffice">
              {chamberIcon &&
                <div
                  className="profile__bullet"
                  alt=""
                  style={{ backgroundImage: `url(${chamberIcon})` }}
                />
              }
              <div className="profile__memberoffice--lbl">
                {entity.chamber}
              </div>
            </div>
            <div className="profile__memberoffice">
              <div className={entityStateIcon} />
              <div className="profile__memberoffice--lbl">
                {entity.state} {getDistrict(entity.district)}
              </div>
            </div>
          </div>
        ) : (
          ('' !== entity.headquarters &&
            <div className="profile__memberoffices">
              <div className="profile__memberoffice">
                <div className="profile__memberoffice--lbl">
                  {entity.headquarters}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    );
  }

  /**
   * @returns {object} - JSX for the component.
   */
  render() {
    const {
      contributions,
      companiesMap,
      handleTableSortClick,
      entity,
      imageBase,
      type,
    } = this.props;
    if (entity.id === undefined) {
      return (
        <div className="campaignFinance__profile" />
      );
    }

    // Negotiate image URL.
    const imageUrl = 'MEMBER' === type ?
      `${imageBase}/members/${entity.id}.jpg` :
      `${imageBase}/companies/${entity.id}.jpg`;
    // Campaign cycles are defined every other year, so we do currentCycle-1 to get the start year
    const cycle = `${contributions.firstCycle - 1}-${contributions.currentCycle}`; // eslint-disable-line max-len
    const tableHeader = ('MEMBER' === type) ?
      `CAMPAIGN CONTRIBUTIONS FROM PHARMACEUTICAL COMPANIES, ${cycle}` :
      `CAMPAIGN CONTRIBUTIONS TO MEMBERS OF CONGRESS, ${cycle}`;

    return (
      <div className="campaignFinance__profile">
        <div className="profile__container">
          <div className="profile__memberinfo">
            {('MEMBER' === type || entity.logoExists) && (
              <div className="profile__image">
                <img
                  alt=""
                  src={imageUrl}
                />
              </div>
            )}
            {this.getHeader()}
            <div className="profile__memberStats">
              <div className="profile__statrow">
                <div className="profile__statrow--label">
                  <div className="profile__strike" />
                  <div className="profile__statrow--text">
                    current cycle
                  </div>
                </div>
                <div className="profile__statrow--value">
                  {getFormattedTotal(contributions.totals.currentCycle)}
                </div>
              </div>
              <div className="profile__statrow">
                <div className="profile__statrow--label">
                  <div className="profile__strike" />
                  <div className="profile__statrow--text">
                    total since {contributions.firstCycle - 1}
                  </div>
                </div>
                <div className="profile__statrow--value">
                  {getFormattedTotal(contributions.totals.grandTotal)}
                </div>
              </div>
            </div>
          </div>
          <div className="profile__table-divider">
            <div className="profile__strike" />
            <div className="profile__table-divider--lbl">
              {tableHeader}
            </div>
          </div>
          <Table
            companiesMap={companiesMap}
            data={contributions}
            handleTableSortClick={handleTableSortClick}
            imageBase={imageBase}
            type={type}
          />
          <div className="profile__table-footnote">
            <p>
              * Numbers for the most recent cycle include only the most
              current complete quarter. Expect a delay between the end
              of a quarter and a data update.
            </p>
            {'MEMBER' === type ? (
              <p>
                Totals include contributions from PACs that no longer
                exist. Only active company PACs appear in the table.
              </p>
            ) : (
              <p>
                Totals include contributions to lawmakers who have since
                left office. Only active members of Congress appear in the
                table.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

Profile.defaultProps = {
  companiesMap: new Map(),
};

Profile.propTypes = {
  entity: PropTypes.oneOfType([
    PropTypes.shape(MEMBER_SHAPE),
    PropTypes.shape(COMPANY_SHAPE),
  ]).isRequired,
  companiesMap: PropTypes.instanceOf(Map),
  contributions: PropTypes.shape(CONTRIBUTIONS_SHAPE).isRequired,
  handleTableSortClick: PropTypes.func.isRequired,
  imageBase: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};
